import type { ReactElement } from "react";
import { Button } from "@lumi/ui-headless";
import { Info, Play, ThumbsUp, Video } from "lucide-react";
import type { TitleHeroProps } from "./TitleHero";

export interface ActionButtonsProps {
  readonly onPlay: () => void;
  readonly hasTrailer: boolean;
  readonly onPlayTrailer?: () => void;
  readonly watchlistState: TitleHeroProps["watchlistState"];
  readonly likeState: TitleHeroProps["likeState"];
}

/**
 * Action Buttons Component
 *
 * Keyboard shortcuts are handled globally by the Title page:
 * - Space/Enter: Play
 * - W: Toggle watchlist
 * - Cmd+L: Toggle like
 */
export function ActionButtons({
  onPlay,
  hasTrailer,
  onPlayTrailer,
  watchlistState,
  likeState,
}: ActionButtonsProps): ReactElement {
  return (
    <div className="grid grid-cols-4 gap-2 md:gap-3 lg:gap-4 pb-0">
      <Button variant="ghost-sm" onClick={onPlay} className="w-full">
        <Play size={20} className="lg:w-5 lg:h-5 mr-0 lg:mr-2" />
        <span className="hidden lg:inline text-sm lg:text-md font-semibold">Play</span>
      </Button>
      {hasTrailer && (
        <Button variant="ghost-sm" onClick={onPlayTrailer} className="w-full">
          <Video size={20} className="lg:w-5 lg:h-5 mr-0 lg:mr-2" />
          <span className="hidden lg:inline text-sm lg:text-md font-semibold">Trailer</span>
        </Button>
      )}
      <Button
        variant="ghost-sm"
        onClick={() => {
          void watchlistState.toggle();
        }}
        disabled={watchlistState.mutating}
        className="w-full"
      >
        <Info
          size={20}
          className="lg:w-5 lg:h-5 mr-0 lg:mr-2"
          color={watchlistState.inWatchlist ? "#F3B33B" : "currentColor"}
        />
        <span className="hidden lg:inline text-sm lg:text-md font-semibold">
          {watchlistState.inWatchlist ? "In List" : "Add to List"}
        </span>
      </Button>
      <Button
        variant="ghost-sm"
        onClick={() => {
          void likeState.toggle();
        }}
        disabled={likeState.mutating}
        className="w-full"
      >
        <ThumbsUp
          size={20}
          className="lg:w-5 lg:h-5 mr-0 lg:mr-2"
          color={likeState.liked ? "#F3B33B" : "currentColor"}
        />
        <span className="hidden lg:inline text-sm lg:text-md font-semibold">{likeState.liked ? "Liked" : "Like"}</span>
      </Button>
    </div>
  );
}
